import React, { useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import API_URL from "../apiConfig";

// Utility: Format date dd-mm-yyyy
function formatDate(dateStr) {
  if (!dateStr) return "";
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) return dateStr;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y, m, d] = dateStr.split("-");
    return `${d}-${m}-${y}`;
  }
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
}

export default function ReceiptPaymentReport({ token }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [receiptHeads, setReceiptHeads] = useState([]);
  const [paymentHeads, setPaymentHeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Editable balances
  const [openingBalance, setOpeningBalance] = useState(0);
  const [cashInBank, setCashInBank] = useState(0);
  const [cashInHand, setCashInHand] = useState(0);

  const [closingCashAtBank, setClosingCashAtBank] = useState(0);
  const [closingCashInHand, setClosingCashInHand] = useState(0);

  function groupByHead(entries) {
    const groups = {};
    for (const entry of entries) {
      const head = entry.account_head || entry.account || "Unknown";
      if (!groups[head]) groups[head] = 0;
      groups[head] += Number(entry.amount) || 0;
    }
    return Object.entries(groups).map(([account_head, total]) => ({
      account_head,
      total,
    }));
  }

  const fetchReport = async () => {
    setLoading(true);
    setError("");
    let fromParam = from ? `&from=${from}` : "";
    let toParam = to ? `&to=${to}` : "";

    try {
      const incomeRes = await fetch(
        `${API_URL}/api/income_expense/entries?type=income${fromParam}${toParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!incomeRes.ok) throw new Error("Failed to fetch income entries");
      const incomeEntries = await incomeRes.json();
      setReceiptHeads(groupByHead(incomeEntries));

      const expenseRes = await fetch(
        `${API_URL}/api/income_expense/entries?type=expense${fromParam}${toParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!expenseRes.ok) throw new Error("Failed to fetch expense entries");
      const expenseEntries = await expenseRes.json();
      setPaymentHeads(groupByHead(expenseEntries));
    } catch (err) {
      setError(err.message);
      setReceiptHeads([]);
      setPaymentHeads([]);
    }
    setLoading(false);
  };

  // Calculate totals including manual balances
  const allReceipts = [
    { account_head: "Opening Balance", total: Number(openingBalance) || 0 },
    { account_head: "Cash in Bank", total: Number(cashInBank) || 0 },
    { account_head: "Cash in Hand", total: Number(cashInHand) || 0 },
    ...receiptHeads,
  ];
  const allPayments = [
    ...paymentHeads,
    { account_head: "Cash at Bank", total: Number(closingCashAtBank) || 0 },
    { account_head: "Cash in Hand", total: Number(closingCashInHand) || 0 },
  ];

  const maxRows = Math.max(allReceipts.length, allPayments.length);

  const totalReceipt = allReceipts.reduce(
    (sum, row) => sum + (Number(row.total) || 0),
    0
  );
  const totalPayment = allPayments.reduce(
    (sum, row) => sum + (Number(row.total) || 0),
    0
  );

  // PDF export logic
  const handleDownloadPDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "A4",
    });

    // Header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.text("AADHAR FOUNDATION'S", 420, 50, { align: "center" });
    doc.setFontSize(14);
    doc.text("SAROJINI COLLEGE OF NURSING, DHULE", 420, 70, { align: "center" });
    doc.setFontSize(12);
    doc.setTextColor(255, 0, 0);
    doc.text("RECEIPT & PAYMENT AC", 420, 90, { align: "center" });
    doc.setFontSize(11);
    doc.text(
      `AS ON ${from ? formatDate(from) : ""} TO ${to ? formatDate(to) : ""}`,
      420,
      110,
      { align: "center" }
    );
    doc.setTextColor(0, 0, 0);

    // Prepare Table Data
    const tableRows = [];
    for (let i = 0; i < maxRows; i++) {
      const receipt = allReceipts[i] || {};
      const payment = allPayments[i] || {};
      tableRows.push([
        receipt.account_head ? `To ${receipt.account_head}` : "",
        receipt.total
          ? Number(receipt.total).toLocaleString("en-IN", { minimumFractionDigits: 2 })
          : "",
        payment.account_head ? `By ${payment.account_head}` : "",
        payment.total
          ? Number(payment.total).toLocaleString("en-IN", { minimumFractionDigits: 2 })
          : "",
      ]);
    }
    // Totals
    tableRows.push([
      { content: "Total", styles: { fontStyle: "bold" } },
      {
        content: totalReceipt.toLocaleString("en-IN", { minimumFractionDigits: 2 }),
        styles: { fontStyle: "bold" },
      },
      { content: "Total", styles: { fontStyle: "bold" } },
      {
        content: totalPayment.toLocaleString("en-IN", { minimumFractionDigits: 2 }),
        styles: { fontStyle: "bold" },
      },
    ]);

    // Table
    autoTable(doc, {
      head: [
        [
          { content: "RECEIPT", colSpan: 2, styles: { halign: "center", fillColor: [220, 230, 241] } },
          { content: "PAYMENT", colSpan: 2, styles: { halign: "center", fillColor: [220, 230, 241] } },
        ],
      ],
      body: tableRows,
      startY: 130,
      styles: {
        font: "helvetica",
        fontSize: 10,
        cellPadding: { top: 4, right: 6, bottom: 4, left: 6 },
        lineColor: [200, 200, 200],
        lineWidth: 0.5,
      },
      columnStyles: {
        0: { halign: "left", cellWidth: 180 },
        1: { halign: "right", cellWidth: 90 },
        2: { halign: "left", cellWidth: 180 },
        3: { halign: "right", cellWidth: 90 },
      },
      theme: "grid",
      didDrawPage: (data) => {
        // Border around the header section
        doc.setDrawColor(0, 128, 0);
        doc.setLineWidth(1);
        doc.rect(40, 30, 760, 100);
      },
    });

    doc.save("Receipt_Payment_Report.pdf");
  };

  return (
    <div className="reports-section">
      <h3>Receipt & Payment Report</h3>
      <div style={{ marginBottom: 12 }}>
        <input
          type="date"
          value={from}
          onChange={e => setFrom(e.target.value)}
        />
        <input
          type="date"
          value={to}
          onChange={e => setTo(e.target.value)}
          style={{ marginLeft: 8 }}
        />
        <button
          style={{ marginLeft: 8 }}
          onClick={fetchReport}
          disabled={loading || !from || !to}
        >
          {loading ? "Loading..." : "Get Report"}
        </button>
        {(receiptHeads.length > 0 || paymentHeads.length > 0) && (
          <button
            style={{
              marginLeft: 8,
              background: "#224488",
              color: "#fff",
              border: "none",
              padding: "6px 16px",
              borderRadius: 4,
              cursor: "pointer"
            }}
            onClick={handleDownloadPDF}
          >
            Download PDF
          </button>
        )}
      </div>
      <div style={{ display: "flex", gap: 40, marginBottom: 12, flexWrap: "wrap" }}>
        <div>
          <label>
            To Opening Balance:{" "}
            <input
              type="number"
              value={openingBalance}
              onChange={e => setOpeningBalance(e.target.value)}
              style={{ width: 120, marginLeft: 4 }}
            />
          </label>
        </div>
        <div>
          <label>
            Cash in Bank:{" "}
            <input
              type="number"
              value={cashInBank}
              onChange={e => setCashInBank(e.target.value)}
              style={{ width: 120, marginLeft: 4 }}
            />
          </label>
        </div>
        <div>
          <label>
            Cash in Hand:{" "}
            <input
              type="number"
              value={cashInHand}
              onChange={e => setCashInHand(e.target.value)}
              style={{ width: 120, marginLeft: 4 }}
            />
          </label>
        </div>
        <div>
          <label>
            Cash at Bank (Closing):{" "}
            <input
              type="number"
              value={closingCashAtBank}
              onChange={e => setClosingCashAtBank(e.target.value)}
              style={{ width: 120, marginLeft: 4 }}
            />
          </label>
        </div>
        <div>
          <label>
            Cash in Hand (Closing):{" "}
            <input
              type="number"
              value={closingCashInHand}
              onChange={e => setClosingCashInHand(e.target.value)}
              style={{ width: 120, marginLeft: 4 }}
            />
          </label>
        </div>
      </div>
      {loading && <div>Loading...</div>}
      {error && <div style={{ color: "red" }}>{error}</div>}
      {(receiptHeads.length > 0 || paymentHeads.length > 0) && (
        <div style={{ overflowX: "auto", marginTop: 24 }}>
          <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "1.2em", lineHeight: "1.25" }}>
            AADHAR FOUNDATION'S<br />
            SAROJINI COLLEGE OF NURSING, DHULE<br />
            RECEIPT & PAYMENT AC
            <br />
            <span style={{ color: "red", fontWeight: 600 }}>
              AS ON {from ? formatDate(from) : ""} TO {to ? formatDate(to) : ""}
            </span>
          </div>
          <table className="iem-table" style={{ maxWidth: 800, margin: "32px auto 0 auto" }}>
            <thead>
              <tr>
                <th colSpan={2} style={{ textAlign: "center" }}>RECEIPT</th>
                <th colSpan={2} style={{ textAlign: "center" }}>PAYMENT</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: maxRows }).map((_, i) => (
                <tr key={i}>
                  <td style={{ textAlign: "left" }}>
                    {allReceipts[i] ? "To " + allReceipts[i].account_head : ""}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {allReceipts[i]
                      ? Number(allReceipts[i].total).toLocaleString("en-IN", { minimumFractionDigits: 2 })
                      : ""}
                  </td>
                  <td style={{ textAlign: "left" }}>
                    {allPayments[i] ? "By " + allPayments[i].account_head : ""}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {allPayments[i]
                      ? Number(allPayments[i].total).toLocaleString("en-IN", { minimumFractionDigits: 2 })
                      : ""}
                  </td>
                </tr>
              ))}
              <tr style={{ fontWeight: "bold" }}>
                <td>Total</td>
                <td style={{ textAlign: "right" }}>
                  {totalReceipt.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </td>
                <td>Total</td>
                <td style={{ textAlign: "right" }}>
                  {totalPayment.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {!loading &&
        receiptHeads.length === 0 &&
        paymentHeads.length === 0 && <div>No data found.</div>}
    </div>
  );
}